import * as React from 'react';

import { AgentType, ModelProvider } from '@rematter/frontend-lib-api-client';
import {
  Checkbox,
  Content,
  ContentCard,
  ContentSection,
  NumberField,
  TagGroup,
  TextField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $agentDetailForm,
  $isCreateMode,
  $updateAgentDetailForm,
} from '../../_state/agentDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

export const AgentTabDetails = () => {
  const t = useTranslations();
  const isCreate = useAtomValue($isCreateMode);
  const formData = useAtomValue($agentDetailForm);
  const updateFormData = useSetAtom($updateAgentDetailForm);

  return (
    <Content className="@AgentTabDetails space-y-6 p-4">
      <ContentCard>
        {/* Status Section */}
        <ContentSection
          title={t.aiManagement.agents.details.sections.basicInfo}
        >
          <div className="grid grid-cols-4 gap-4">
            {!isCreate && (
              <div>
                <TagGroup
                  selectionMode="single"
                  label={t.aiManagement.agents.details.fields.isActive.label}
                  items={[
                    {
                      key: 'active',
                      label: t.aiManagement.agents.status.active,
                    },
                    {
                      key: 'inactive',
                      label: t.aiManagement.agents.status.inactive,
                    },
                  ]}
                  selectedKeys={[formData.isActive ? 'active' : 'inactive']}
                  onSelectionChange={(v) => {
                    updateFormData({ isActive: v[0] === 'active' });
                  }}
                />
              </div>
            )}

            {isCreate && (
              <div>
                <TagGroup
                  selectionMode="single"
                  label={t.aiManagement.agents.details.fields.type.label}
                  items={[
                    {
                      key: 'Agent',
                      label:
                        t.aiManagement.agents.details.fields.type.agent.label,
                    },
                    {
                      key: 'SubAgent',
                      label:
                        t.aiManagement.agents.details.fields.type.subAgent
                          .label,
                    },
                    {
                      key: 'FileProcessor',
                      label:
                        t.aiManagement.agents.details.fields.type.fileProcessor
                          .label,
                    },
                  ]}
                  selectedKeys={[formData.type]}
                  onSelectionChange={(v) =>
                    updateFormData({
                      type: v[0] as AgentType,
                      agentIdentifier: v[0] === 'SubAgent' ? '' : undefined,
                    })
                  }
                />
              </div>
            )}

            <TextField
              aria-label={t.aiManagement.agents.details.fields.name.label}
              label={t.aiManagement.agents.details.fields.name.label}
              placeholder={
                t.aiManagement.agents.details.fields.name.placeholder
              }
              value={formData.name}
              onChange={(value) => updateFormData({ name: value })}
              isRequired
              autoFocus
            />

            {formData.type === 'SubAgent' && (
              <TextField
                aria-label={
                  t.aiManagement.agents.details.fields.agentIdentifier.label
                }
                label={
                  t.aiManagement.agents.details.fields.agentIdentifier.label
                }
                placeholder={
                  t.aiManagement.agents.details.fields.agentIdentifier
                    .placeholder
                }
                value={formData.agentIdentifier ?? ''}
                onChange={(value) =>
                  updateFormData({ agentIdentifier: value || undefined })
                }
                isRequired
                errorMessage={
                  formData.agentIdentifier &&
                  !/^[a-z0-9-]+$/.test(formData.agentIdentifier)
                    ? t.aiManagement.agents.details.fields.agentIdentifier
                        .validation.pattern
                    : undefined
                }
              />
            )}
          </div>
        </ContentSection>

        {/* AI Model Configuration Section */}
        <ContentSection
          title={t.aiManagement.agents.details.sections.modelConfig}
        >
          <div className="grid grid-cols-4 items-end gap-4">
            <div>
              <TagGroup
                selectionMode="single"
                label={t.aiManagement.agents.details.fields.provider.label}
                items={[
                  {
                    key: 'Gemini',
                    label:
                      t.aiManagement.agents.details.fields.provider.options
                        .gemini,
                  },
                  {
                    key: 'OpenAI',
                    label:
                      t.aiManagement.agents.details.fields.provider.options
                        .openai,
                  },
                  {
                    key: 'Anthropic',
                    label:
                      t.aiManagement.agents.details.fields.provider.options
                        .anthropic,
                  },
                  {
                    key: 'Cerebras',
                    label:
                      t.aiManagement.agents.details.fields.provider.options
                        .cerebras,
                  },
                ]}
                selectedKeys={[
                  (formData.modelProvider as ModelProvider) ?? 'Gemini',
                ]}
                onSelectionChange={(v) => {
                  updateFormData({ modelProvider: v[0] as ModelProvider });
                }}
              />
            </div>

            <TextField
              aria-label={t.aiManagement.agents.details.fields.model.label}
              label={t.aiManagement.agents.details.fields.model.label}
              placeholder={
                t.aiManagement.agents.details.fields.model.placeholder
              }
              value={formData.model ?? ''}
              onChange={(value) => updateFormData({ model: value })}
              isRequired
            />

            <TextField
              aria-label={t.aiManagement.agents.details.fields.apiKey.label}
              label={t.aiManagement.agents.details.fields.apiKey.label}
              placeholder={
                t.aiManagement.agents.details.fields.apiKey.placeholder
              }
              value={formData.providerApiKey ?? ''}
              onChange={(value) =>
                updateFormData({ providerApiKey: value || undefined })
              }
            />

            <TextField
              aria-label={t.aiManagement.agents.details.fields.baseUrl.label}
              label={t.aiManagement.agents.details.fields.baseUrl.label}
              placeholder={
                t.aiManagement.agents.details.fields.baseUrl.placeholder
              }
              value={formData.providerBaseUrl ?? ''}
              onChange={(value) =>
                updateFormData({ providerBaseUrl: value || undefined })
              }
            />

            <NumberField
              aria-label={
                t.aiManagement.agents.details.fields.temperature.label
              }
              label={t.aiManagement.agents.details.fields.temperature.label}
              value={formData.temperature ?? 0.7}
              onChange={(value) => updateFormData({ temperature: value })}
              minValue={0}
              maxValue={2}
              step={0.1}
              formatOptions={{
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }}
            />

            <div className="flex h-9.5 items-center rounded-lg border border-neutral/40 bg-white px-3 py-2">
              <Checkbox
                isSelected={formData.reasoningEnabled ?? false}
                onChange={(value) =>
                  updateFormData({ reasoningEnabled: value })
                }
                className="mr-3"
              >
                <span className="text-sm text-neutral/90">
                  {t.aiManagement.agents.details.fields.reasoning.label}
                </span>
              </Checkbox>
            </div>

            {formData.reasoningEnabled && (
              <NumberField
                aria-label={
                  t.aiManagement.agents.details.fields.reasoningBudget.label
                }
                label={
                  t.aiManagement.agents.details.fields.reasoningBudget.label
                }
                value={formData.reasoningBudget ?? 1024}
                onChange={(value) => updateFormData({ reasoningBudget: value })}
                minValue={1}
                step={1}
              />
            )}
          </div>
        </ContentSection>
      </ContentCard>
    </Content>
  );
};
